import type { GetThemePerformance } from '@api';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useGetThemePerformance } from '@terminal/react-query-hooks/themes/useGetThemePerformance';
import { formatNumber } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';
import clsx from 'clsx';
import React from 'react';

import { SimpleHorizon } from '../simple-horizon-selector/SimpleHorizonSelector';
import styles from './PriceReturnPill.module.scss';

type PerformanceKey = keyof GetThemePerformance['performance'];

/** API uses `1y` for the 12-month return; UI horizon is `12M`. */
const SIMPLE_HORIZON_TO_PERFORMANCE_KEY: Record<SimpleHorizon, PerformanceKey> =
  {
    '1D': '1d',
    '1W': '1w',
    '1M': '1m',
    '3M': '3m',
    '6M': '6m',
    '12M': '1y',
  };

interface PriceReturnPillProps {
  horizon: SimpleHorizon;
  themeId: string;
}

export const PriceReturnPill = ({ horizon, themeId }: PriceReturnPillProps) => {
  const { data, isLoading } = useGetThemePerformance({ themeId });
  const performanceKey = SIMPLE_HORIZON_TO_PERFORMANCE_KEY[horizon];
  const performanceValue = data?.performance[performanceKey];

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '20px',
            },
          },
        ]}
      />
    );
  }

  if (performanceValue === undefined) {
    return null;
  }

  const value = formatNumber({
    isPercentage: true,
    value: performanceValue,
    addPlus: true,
  });
  const isNegative = performanceValue < 0;

  return (
    <div
      className={clsx(styles.rightHeaderWrapper, {
        [styles.negative]: isNegative,
      })}
    >
      <span className={styles.rightHeaderReturnLabel}>{horizon}</span>
      <span
        className={clsx(styles.rightHeaderReturnValue, {
          [styles.negative]: isNegative,
        })}
      >
        {value}
      </span>
    </div>
  );
};
